'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { memo, type ReactNode, useEffect, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { MonitorStopIcon as DesktopApp } from '@air/design-icons';
import { VisuallyHidden } from '@air/primitive-visually-hidden';

import { BROWSER_TAB_ROUTES } from '~/constants/routes';

/**
 * Brief window we give Adobe UXP to inject `window.uxpHost` before deciding a
 * page load is in a regular browser. 500 ms handles cold-start races where the
 * webview element mounts slightly before the bridge is wired up.
 */
const UXP_HOST_WAIT_MS = 500;

// See JSDoc on `BROWSER_TAB_ROUTES` in `~/constants/routes.ts` for rationale.

/**
 * Internal-testing escape hatch. Appending `?previewInBrowser=1` to any URL
 * on a non-production deploy (development / preview / QA) bypasses the gate
 * for the remainder of the tab session, so internal users can walk the panel
 * UI in a regular browser without standing up Premiere. Honored only when
 * `NEXT_PUBLIC_VERCEL_ENV !== 'production'` — the param is silently ignored
 * on the prod URL, so the user-facing gate stays hard.
 */
const PREVIEW_QUERY_PARAM = 'previewInBrowser';
const PREVIEW_SESSION_KEY = 'air:preview-in-browser';
const IS_PREVIEW_ENVIRONMENT = process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production';

const hasUxpHost = (): boolean => {
  return typeof window !== 'undefined' && !!window.uxpHost;
};

const hasPreviewSessionFlag = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.sessionStorage.getItem(PREVIEW_SESSION_KEY) === '1';
  } catch {
    // Storage can throw in sandboxed iframes or with cookies disabled — fail closed.
    return false;
  }
};

const setPreviewSessionFlag = () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(PREVIEW_SESSION_KEY, '1');
  } catch {
    // Ignore — the URL param will keep working even if persistence fails.
  }
};

const BrowserBlockedSplash = memo(() => (
  <main className="flex h-screen flex-col items-center justify-center gap-3 bg-neutral-1 p-8 text-center text-neutral-12">
    <div className="flex size-12 items-center justify-center rounded-12 bg-neutral-3">
      <DesktopApp aria-hidden="true" className="text-neutral-10" size="lg" />
    </div>
    <h1 className="font-semibold text-16">Open in Adobe Premiere Pro</h1>
    <p className="max-w-sm text-13 text-neutral-10">
      The Air panel is only available inside Adobe Premiere Pro. Install the plugin from the Creative Cloud Marketplace,
      then open it from Premiere to continue.
    </p>
  </main>
));

BrowserBlockedSplash.displayName = 'BrowserBlockedSplash';

/**
 * Baked-in production flag. Next.js (and Cypress's webpack) inline
 * `process.env.NODE_ENV` at build time, so reading it inside a component
 * body is effectively a literal — it cannot be stubbed at runtime. Capturing
 * it in a module constant and accepting it as a prop lets tests override the
 * production path explicitly.
 */
const IS_PRODUCTION_BUILD = process.env.NODE_ENV === 'production';

export type BrowserGateProps = {
  children: ReactNode;
  /**
   * Override the production check. Defaults to `NODE_ENV === 'production'`.
   * Exposed so component tests can exercise both branches without relying on
   * build-time environment rewriting.
   */
  isProduction?: boolean;
};

/**
 * Renders children only when the Air panel is loaded inside Adobe Premiere
 * Pro's UXP WebView (detected via `window.uxpHost`). Otherwise shows a splash
 * telling the user to open the plugin from Premiere.
 *
 * In development (`NODE_ENV !== 'production'`) the gate is a no-op so the app
 * remains reachable at `localhost:3007` for browser-based testing.
 *
 * `/oauth-callback` is exempt by design — Cognito redirects there after a
 * Google/Apple SSO round-trip and the page deliberately runs in the system
 * browser (where `window.uxpHost` is absent). Blocking it would hang the
 * plugin's polling forever.
 *
 * Non-prod deploys honor a `?previewInBrowser=1` URL param (persisted to
 * sessionStorage so in-app navigation keeps working) as an internal-only
 * preview hatch. Production ignores the param.
 */
export const BrowserGate = memo(({ children, isProduction = IS_PRODUCTION_BUILD }: BrowserGateProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isExemptPath = BROWSER_TAB_ROUTES.has(pathname);

  // Query flag participates in bypass on the initial render so SSR/pre-hydration
  // markup matches Next's search params and we don't briefly show the gated
  // state before an effect fires. Session storage can't be read reliably on the
  // server; sync it before paint via useIsomorphicLayoutEffect (see below).
  const previewBypassFromQuery = IS_PREVIEW_ENVIRONMENT && searchParams.get(PREVIEW_QUERY_PARAM) === '1';

  const [previewBypassFromStorage, setPreviewBypassFromStorage] = useState(false);

  useIsomorphicLayoutEffect(() => {
    if (!IS_PREVIEW_ENVIRONMENT) {
      return;
    }
    const hasQueryFlag = searchParams.get(PREVIEW_QUERY_PARAM) === '1';

    if (hasQueryFlag) {
      setPreviewSessionFlag();
      // Strip the param so the URL stays clean (and a paste/bookmark of the
      // current view doesn't keep leaking the bypass to other people).
      const next = new URLSearchParams(searchParams.toString());

      next.delete(PREVIEW_QUERY_PARAM);
      const search = next.toString();

      router.replace(`${pathname}${search ? `?${search}` : ''}`);
    }

    setPreviewBypassFromStorage(hasPreviewSessionFlag());
  }, [pathname, router, searchParams]);

  const isGateActive = isProduction && !isExemptPath && !(previewBypassFromQuery || previewBypassFromStorage);

  const [status, setStatus] = useState<'checking' | 'allowed' | 'blocked'>(() =>
    isGateActive ? 'checking' : 'allowed',
  );

  useEffect(() => {
    if (!isGateActive) {
      setStatus('allowed');
      return;
    }

    if (hasUxpHost()) {
      setStatus('allowed');
      return;
    }

    const timer = setTimeout(() => {
      setStatus(hasUxpHost() ? 'allowed' : 'blocked');
    }, UXP_HOST_WAIT_MS);

    return () => clearTimeout(timer);
  }, [isGateActive]);

  // When the gate logic says we're not blocking (non-prod, exempt paths,
  // internal preview bypass), never flash the interim UXP-detection shell.
  if (!isGateActive) {
    return <>{children}</>;
  }

  if (status === 'checking') {
    // Render nothing visible during the UXP-detection wait, but expose an
    // off-screen status to screen readers so they don't sit silent on a
    // blank page mid-probe.
    return (
      <VisuallyHidden>
        <output aria-busy aria-live="polite">
          Checking Premiere bridge…
        </output>
      </VisuallyHidden>
    );
  }

  if (status === 'blocked') {
    return <BrowserBlockedSplash />;
  }

  return <>{children}</>;
});

BrowserGate.displayName = 'BrowserGate';
